/**
 * Copyright (C) 2018 Egylis,
 * Siège social, 46 rue René Clair, 75892 PARIS cedex 18 - France.
 * All Rights Reserved.
 *
 * E-mail : info@egylis.com
 *
 * This software is the confidential and proprietary information
 * of Egylis. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only
 * in accordance with the terms of the license agreement you
 * entered into with Egylis.
 *
 * Created by calvez on 16/11/18.
 *
 * Version : Generator 2.0.9
 */
import {Injectable, NgZone} from '@angular/core';
import {GeoMode} from './entity/geoMode';
import {StatusEnum} from '../entity';
import {Country, Street, ZipCode} from './entity';
import {ConfigService, CordovaBackService, CordovaUIService} from '../service';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {BehaviorSubject} from 'rxjs';
import {FileJournal, SynchroStreamInfo} from '../msa/entity';
import {TranslateService} from '@ngx-translate/core';
import {LogService} from '../log';

/**
 * GeoService
 */
@Injectable()
export class GeoService {
    /**
     * Is started
     */
    private _isStarted: boolean = false;
    /**
     * Current GeoMode
     * @type {GeoMode.NONE}
     * @private
     */
    private _currentMode: GeoMode = GeoMode.NONE;
    /**
     * GeoMode
     * @type {GeoMode.NONE}
     * @private
     */
    private _wantedMode: GeoMode = GeoMode.NONE;

    /**
     * On Resume Stream.
     */
    private infoStream: BehaviorSubject<SynchroStreamInfo>;
    /**
     * Done Current Item Size
     */
    private currentItemSizeDone: number = 0;
    /**
     * Index.
     */
    private index: number = 0;
    /**
     * Geo File
     * @type {null}
     */
    private geoFile: FileJournal = null;


    /**
     * Constructor
     */
    constructor(private configService: ConfigService,
                private cordovaBackService: CordovaBackService,
                private ngZone: NgZone,
                private http: HttpClient,
                private cordovaService: CordovaUIService,
                private translate: TranslateService,
                private logService: LogService) {
        this.logService.logLog('*** GeoService - constructor', 'GeoService', 'constructor', 2);
        this.infoStream = new BehaviorSubject<SynchroStreamInfo>(null);
        this.initData();
    }

    /**
     * Init Data
     */
    private initData() {
        this.logService.logLog('*** GeoService - initData', 'GeoService', 'initData', 2);
        this.configService.isReadyToStart().then(
            () => {
                this.initDataGeo();
            },
            () => {
                // TODO gérer l'erreur
            }
        );
    }

    /**
     * Init Data Geo
     */
    private initDataGeo() {
        this.logService.logLog('*** GeoService - initDataGeo', 'GeoService', 'initDataGeo', 2);
        switch (this._wantedMode) {
            case GeoMode.WEB:
            case GeoMode.NONE:
            default :
                this._isStarted = true;
                break;
        }
    }

    /**
     * Return a promise who resolve when it's ready to start.
     * @return {Promise<StatusEnum>}
     */
    public isReadyToStart(): Promise<StatusEnum> {
        this.logService.logLog('*** GeoService - isReadyToStart ========',
            'GeoService', 'isReadyToStart', 3);
        return new Promise(
            (resolve) => {
                this.isStartedTimeout(resolve);
            });
    }


    /**
     * is Started with a timeout
     * @param promiseResolve
     */
    private isStartedTimeout(promiseResolve: (r: StatusEnum) => void): void {
        this.logService.logLog('*** GeoService - isStartedTimeout',
            'GeoService', 'isStartedTimeout', 3);
        if (this._isStarted) {
            this.logService.logLog('*** GeoService - isStartedTimeout -- OK',
                'GeoService', 'isStartedTimeout', 3);
            promiseResolve(StatusEnum.OK);
        } else {
            this.logService.logLog('*** GeoService - isStartedTimeout -- TimeOut',
                'GeoService', 'isStartedTimeout', 3);
            setTimeout(this.isStartedTimeout.bind(this, promiseResolve), 100);
        }
    }


    /**
     * Get Country by langage Code
     * @param {string} langageCode
     * @returns {Promise<Array<Country>>}
     */
    public getCountry(langageCode: string): Promise<Array<Country>> {
        let res: Promise<Array<Country>>;
        switch (this._currentMode) {
            case GeoMode.WEB:
                res = this.getCountryModeWeb(langageCode);
                break;
            default:
                res = this.getCountryModeWeb(langageCode);
                break;
        }
        return res;
    }

    /**
     * Get City by langage Code
     * @param {string} countryCode
     * @param {string} zipCode
     * @returns {Promise<Array<Country>>}
     */
    public getCity(countryCode: string, zipCode: string): Promise<Array<ZipCode>> {
        let res: Promise<Array<Country>>;
        switch (this._currentMode) {
            case GeoMode.WEB:
                res = this.getCityModeWeb(countryCode, zipCode);
                break;
            default:
                res = this.getCityModeWeb(countryCode, zipCode);
                break;
        }
        return res;
    }

    /**
     * Get Street
     * @param {string} countryCode Counrty Code
     * @param {string} adminCode admin Code (insee in FR, zipcode in BELUX)
     * @param {string} searchTerm search term on street name
     * @param {string} pageNumber page number
     * @param {string} pageSize page size
     * @returns {Promise<Array<Country>>}
     */
    public getStreet(countryCode: string,
                     adminCode: string,
                     searchTerm: string,
                     pageNumber: number,
                     pageSize: number): Promise<Array<Street>> {
        let res: Promise<Array<Street>>;
        switch (this._currentMode) {
            case GeoMode.WEB:
                res = this.getStreetModeWeb(countryCode, adminCode, searchTerm, pageNumber, pageSize);
                break;
            default:
                res = this.getStreetModeWeb(countryCode, adminCode, searchTerm, pageNumber, pageSize);
                break;
        }
        return res;
    }


    /**
     * Get Country By Web
     * @param {string} langageCode
     * @returns {Promise<Array<Country>>}
     */
    private getCountryModeWeb(langageCode: string): Promise<Array<Country>> {
        return new Promise(
            (resolve, reject) => {
                this.http.get<Array<Country>>(
                    `${environment.geoServerUrl}geo/rest/geo/l/${encodeURIComponent(String(langageCode))}/country`)
                    .subscribe(values => {
                            resolve(values);
                        },
                        () => {
                            reject();
                        });
            });
    }


    /**
     * Get City By Web
     * @param {string} countryCode
     * @param {string} zipCode
     * @returns {Promise<Array<Country>>}
     */
    private getCityModeWeb(countryCode: string, zipCode: string): Promise<Array<ZipCode>> {
        return new Promise(
            (resolve, reject) => {
                this.http.get<Array<ZipCode>>(
                    `${environment.geoServerUrl}geo/rest/geo/` +
                    `c/${encodeURIComponent(String(countryCode))}/z/${encodeURIComponent(String(zipCode))}/zipcode`)
                    .subscribe(values => {
                            resolve(values);
                        },
                        () => {
                            reject();
                        });
            });
    }

    /**
     * Get Street By Web
     * @param {string} countryCode Counrty Code
     * @param {string} adminCode admin Code (insee in FR, zipcode in BELUX)
     * @param {string} searchTerm search term on street name
     * @param {string} pageNumber page number
     * @param {string} pageSize page size
     * @returns {Promise<Array<Country>>}
     */
    private getStreetModeWeb(countryCode: string,
                             adminCode: string,
                             searchTerm: string,
                             pageNumber: number,
                             pageSize: number): Promise<Array<Street>> {
        return new Promise(
            (resolve, reject) => {
                this.http.get<Array<Street>>(
                    `${environment.geoServerUrl}geo/rest/geo/c/${encodeURIComponent(String(countryCode))}` +
                    `/ac/${encodeURIComponent(String(adminCode))}` +
                    `/pn/${encodeURIComponent(String(pageNumber))}` +
                    `/ps/${encodeURIComponent(String(pageSize))}/street/` +
                    `s/${encodeURIComponent(String(searchTerm))}`)
                    .subscribe(values => {
                            resolve(values);
                        },
                        () => {
                            reject();
                        });
            });
    }


    /**
     * Get Current Mode
     * @returns {GeoMode}
     */
    public get currentMode(): GeoMode {
        return this._currentMode;
    }
}
